import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { FormShell } from './FormShell';
import { useBridge } from '../bridge/useBridge';

/**
 * Step definition. `fields` lists the form field names belonging to this step
 * so we can validate just that subset before advancing to the next step.
 */
export type Step = {
  id: string;
  label: string;
  component: React.ComponentType;
  /** Field names (paths) that must be valid before leaving this step. */
  fields?: readonly string[];
};

type Props = {
  flowId: string;
  steps: Step[];
  /** Called when the user submits the last step and validation passes. */
  onSubmit?: (values: FieldValues) => Promise<unknown> | unknown;
};

/**
 * StepWizard wires a `react-hook-form` FormProvider (mounted by the parent)
 * to a URL-per-step routing scheme. Each step is a child route under the
 * flow's path: `/flows/:flowId/:stepId`.
 *
 * The wizard emits `step:change` on every transition, listens for `goto` and
 * `back:request` from the host, and emits `submit:success` / `submit:error`
 * on completion.
 */
export const StepWizard: React.FC<Props> = ({ flowId, steps, onSubmit }) => {
  if (steps.length === 0) {
    throw new Error(`StepWizard: flow "${flowId}" has no steps`);
  }
  const firstStepId = steps[0].id;

  return (
    <Routes>
      <Route path="/" element={<Navigate to={firstStepId} replace />} />
      <Route
        path=":stepId"
        element={<StepView flowId={flowId} steps={steps} onSubmit={onSubmit} />}
      />
      <Route path="*" element={<Navigate to={firstStepId} replace />} />
    </Routes>
  );
};

const StepView: React.FC<Props> = ({ flowId, steps, onSubmit }) => {
  const { stepId } = useParams<{ stepId: string }>();
  const navigate = useNavigate();
  const bridge = useBridge();
  const form = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const index = steps.findIndex((s) => s.id === stepId);
  const step = index >= 0 ? steps[index] : undefined;

  // Navigate to a sibling step. React Router v6's default route-relative
  // resolution turns `navigate('email')` from `/flows/hello-world/name` into
  // `/flows/hello-world/name/email` (append, not replace), which then falls
  // through to the wildcard route and bounces back to the first step.
  // `relative: 'path'` + `../` resolves the URL like a regular path segment.
  const goToStep = (targetId: string) => {
    navigate(`../${targetId}`, { relative: 'path' });
  };

  // Announce step change to the host.
  useEffect(() => {
    if (!step) return;
    bridge.emit({
      type: 'step:change',
      flowId,
      stepId: step.id,
      stepIndex: index,
      totalSteps: steps.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id]);

  // Host → Form: jump to a specific step.
  useEffect(() => {
    return bridge.onGoto((targetId) => {
      if (steps.some((s) => s.id === targetId)) {
        goToStep(targetId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge, navigate, steps]);

  // Host → Form: back pressed (e.g. Android hardware button).
  useEffect(() => {
    return bridge.onBackRequest(() => {
      if (index > 0) {
        goToStep(steps[index - 1].id);
      } else {
        bridge.emit({ type: 'cancel', flowId });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge, navigate, steps, index, flowId]);

  if (!step) {
    return <Navigate to={steps[0].id} replace />;
  }

  const isLast = index === steps.length - 1;
  const isFirst = index === 0;

  const goNext = async () => {
    if (isSubmitting) return;

    // Validate only the fields owned by this step.
    if (step.fields && step.fields.length > 0) {
      const ok = await form.trigger(step.fields as unknown as Parameters<typeof form.trigger>[0]);
      if (!ok) return;
    }

    if (!isLast) {
      goToStep(steps[index + 1].id);
      return;
    }

    // Last step → run the full form's submit pipeline.
    setIsSubmitting(true);
    await form.handleSubmit(
      async (values) => {
        try {
          const result = onSubmit ? await onSubmit(values) : values;
          bridge.emit({ type: 'submit:success', flowId, result: result ?? values });
        } catch (e) {
          bridge.emit({
            type: 'submit:error',
            flowId,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      },
      // onInvalid — kept for the last-step case if `fields` was not declared.
      () => {
        // No-op: react-hook-form already set errors on the fields.
      },
    )();
    setIsSubmitting(false);
  };

  const goBack = () => {
    if (isSubmitting) return;
    if (!isFirst) {
      goToStep(steps[index - 1].id);
    } else {
      bridge.emit({ type: 'cancel', flowId });
    }
  };

  const StepComponent = step.component;

  return (
    <FormShell
      title={step.label}
      stepIndex={index}
      totalSteps={steps.length}
      onNext={goNext}
      onBack={goBack}
      nextLabel={isLast ? 'Submit' : 'Next'}
      backLabel={isFirst ? 'Cancel' : 'Back'}
      isSubmitting={isSubmitting}
    >
      <StepComponent />
    </FormShell>
  );
};
